import store from '..'
import axios from 'axios'
import API from '@/common/api'
import imageCompression from 'browser-image-compression'
import keyutils from 'js-crypto-key-utils'
import rsa from 'js-crypto-rsa'

export default {
  namespaced: true,
  state: {
    supportPlu: '',
    pluMethod: ''
  },
  mutations: {
    setPluMethod (state, data) {
      state.pluMethod = data
      localStorage.setItem(`${store.state.authentication.siteid}_plu_filter`, data)
    }
  },
  actions: {
    // 특정 데이터(string)를 암호화 하여 리턴
    async dataEncryption ({}, data) {
      const publicKey = '-----BEGIN PUBLIC KEY-----\n' +
      'MIIBITANBgkqhkiG9w0BAQEFAAOCAQ4AMIIBCQKCAQB3vV2a31LS8bYVPDdQQK3w\n' +
      'T/+Uy8Ejuxoc+9OwQJroNVjtJbjvcZD0gKSQuFYtXB/tDzK9/lGxeQ4TfkSqesdy\n' +
      '31/KrZa54z24sNkbII3x6sY0aow87/eCNpVl3Fw0evqs3REmQUO6eHBgDeH1qk60\n' +
      'oUhYg1ZGySIpw6Di6/+OchQnRKjoCGfQ8HNefWt0f4JtDavGOsXI5UupK4Ol+9sd\n' +
      'XcwkwCQ3L8Cc86Dav+LuEqMNY1m72m8mwElT4faDaatY3056WMwJvAy2IfF2GmOg\n' +
      '3/XCCbhZ/jqajidMBxN0ciAH4jJuH6aNT0HiORpl9Rz78Du4jFSkBzHrz7go5B3f\n' +
      'AgMBAAE=\n' +
      '-----END PUBLIC KEY-----'
      /**
       * pem 형식의 키를 jwk 형식으로 컨버팅
       * publicKey -> keyObj -> jwkKey
       */
      const keyObj = new keyutils.Key('pem', publicKey)
      const jwkKey = await keyObj.export('jwk')
      try {
        const encdoer = new TextEncoder()
        const bytes = encdoer.encode(data)
        const encoded = await rsa.encrypt(bytes, jwkKey, 'SHA-256')
        const b64encoded = btoa(String.fromCharCode.apply(null, encoded))
        return b64encoded
      } catch (error) {
        console.error('Encryption failed:', error)
        throw error // 에러 발생 시 호출 측으로 전달
      }
    },
    async uploadFile({ state }, data) {
      const requestId = store.state.authentication.requestId
      const siteid = store.state.authentication.userInfo.siteid
      const fileId = data.id

      async function handleImageUpload(file) {
        const maxSizeMB = 1
        const max = 1024 * 1024 * 4

        const options = {
          maxSizeMB,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        }

        if (file.size > max) {
          alert(store.state.service.locale.data.validate_upload_file_size)
          return false
        }

        try {
          const isImage = file.type && file.type.includes('image')
          const isGif = file.type && file.type.includes('gif')

          const compressedFile = isImage && !isGif
            ? await imageCompression(file, options)
            : file

          return {
            name: file.name,
            format: file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase(),
            file: compressedFile,
            originalFile: file,
            isImage,
            isGif
          }
        } catch (error) {
          console.log(error)
          return false
        }
      }

      async function createThumbWebp(file, fileName) {
        try {
          const options = {
            maxSizeMB: 0.3,
            maxWidthOrHeight: 480,
            useWebWorker: true,
            fileType: 'image/webp',
            initialQuality: 0.8
          }

          const thumbFile = await imageCompression(file, options)

          return {
            name: `${fileName.replace(/\.[^.]+$/, '')}_thumb.webp`,
            format: 'webp',
            file: thumbFile
          }
        } catch (error) {
          console.log('thumb webp create error', error)
          return null
        }
      }

      async function uploadBinary({ requestId, path, file }) {
        return axios({
          method: 'post',
          url: `${API.BASE_URL}/castit/file?id=${requestId}&file=${path}`,
          headers: {
            'Content-type': 'application/octet-stream'
          },
          data: file
        })
      }

      const res = await handleImageUpload(data.file)

      if (!res) return false

      const originFormat = res.format
      const originPath = `files/${fileId}.${originFormat}`
      const originUrl = `https://istay-guestvalet-file.s3.ap-northeast-2.amazonaws.com/${siteid}/${originPath}`

      let thumbUrl = null

      // 1) 원본 업로드
      await uploadBinary({
        requestId,
        path: originPath,
        file: res.file
      })

      // 2) 썸네일 생성 + 업로드 (이미지고 gif가 아닐 때만)
      if (data.addThumb && res.isImage && !res.isGif) {
        const thumb = await createThumbWebp(res.originalFile, res.name)

        if (thumb) {
          const thumbPath = `files/${fileId}_thumb.webp`
          thumbUrl = `https://istay-guestvalet-file.s3.ap-northeast-2.amazonaws.com/${siteid}/${thumbPath}`

          try {
            await uploadBinary({
              requestId,
              path: thumbPath,
              file: thumb.file
            })
          } catch (error) {
            console.log('thumb upload error', error)
            thumbUrl = null
          }
        }
      }

      return {
        name: res.name,
        format: originFormat,
        source: originUrl,
        ...thumbUrl && {
          thumbSource: thumbUrl
        }
      }
    },
    async getSupportPlu ({ state, commit }, data) {
      const storage = localStorage.getItem(`${store.state.authentication.siteid}_plu_filter`)
      try {
        const res = await axios.post(`${API.BASE_URL}/castit/file`, {
          cmd: 'sc-search-plu',
          func: 'get-support-plu',
          id: store.state.authentication.requestId
        })
        state.supportPlu = res.data.message && !res.data.message.includes('not support') ? res.data.message : ''
        commit('setPluMethod', storage || res.data.message[0])
        return res.data.message
      } catch (ex) {
        // alert(ex)
        console.log(ex)
        return false
      }
    },
    async getPluItems ({ state }, data) {
      try {
        const res = await axios.post(`${API.BASE_URL}/castit/file`, {
          cmd: 'sc-search-plu',
          func: 'get-item-list',
          id: store.state.authentication.requestId,
          data: {
            method: data.method,
            value: data.value,
            ...data.orderType && {
              orderType: data.orderType
            }
          }
        })
        return res.data.errorMessage ? JSON.parse(res.data.errorMessage) : res.data.message
      } catch (ex) {
        // alert(ex)
        console.log(ex)
        return false
      }
    }
  },
  getters: {
    getSupportPlu: (state) => state.supportPlu,
    getPluMethod: (state) => state.pluMethod,
    filteredLanguageValues: (state, getters, rootState, rootGetters) => (langObj) => { // 사이트에서 사용하는 언어값만 객체로 리턴
      const usableLangs = rootGetters['setting/getData'].lang.usable || [] // ['ko', 'en'] 사용하는 언어
      return usableLangs.reduce((acc, lang) => {
        if (langObj[lang]) {
          acc[lang] = langObj[lang]
        }
        return acc
      }, {})
    }
  }
}
